import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { AreaChart, Area, ResponsiveContainer, XAxis, YAxis, Tooltip } from "recharts";
import { Ban, RotateCcw, Pencil, Trash2, BadgeCheck, X, Lock, LockOpen } from "lucide-react";

const API = "/api";
const headers = () => ({
  Authorization: `Bearer ${localStorage.getItem("atr_token") || sessionStorage.getItem("atr_admin_token") || ""}`,
});

const PLANS = [
  ["", "Tidak diubah"],
  ["1m", "1 Bulan — Rp99.000"],
  ["3m", "3 Bulan — Rp249.000"],
  ["12m", "1 Tahun — Rp999.000"],
];
const PLAN_DEFAULT_PRICE = { "1m": 99000, "3m": 249000, "12m": 999000 };

const SymbolsControl = () => {
  const [sym, setSym] = useState(null);
  useEffect(() => {
    axios.get(`${API}/admin/symbols`, { headers: headers() }).then((r) => setSym(r.data)).catch(() => {});
  }, []);
  const toggle = async (s) => {
    const on = sym.active.includes(s);
    const next = on ? sym.active.filter((x) => x !== s) : [...sym.active, s];
    try {
      await axios.put(`${API}/admin/symbols`, { symbols: next }, { headers: headers() });
      setSym({ ...sym, active: next });
      toast.success(on ? `${s} dinonaktifkan — data tetap jalan, sinyal dimatikan` : `${s} diaktifkan kembali`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Gagal mengubah pair");
    }
  };
  if (!sym) return null;
  return (
    <div className="mb-4 border border-white/10 bg-ink-surface px-4 py-3" data-testid="symbols-control">
      <p className="font-mono text-[9px] uppercase tracking-widest text-mute-dim">
        Pair Sinyal Aktif — pair nonaktif: harga & chart tetap jalan, sinyal ke user dimatikan (mode backtest)
      </p>
      <div className="mt-2 flex flex-wrap gap-1.5">
        {sym.all.map((s) => (
          <button key={s} onClick={() => toggle(s)} data-testid={`symbol-toggle-${s.toLowerCase()}`}
            className={`border px-3 py-1.5 font-mono text-xs font-bold transition-colors ${
              sym.active.includes(s) ? "border-buy bg-buy/15 text-buy" : "border-sell/40 bg-sell/5 text-sell line-through"}`}>
            {s}
          </button>
        ))}
      </div>
    </div>
  );
};

const Card = ({ label, value, cls = "text-white", sub, testid }) => (
  <div className="border border-white/10 bg-ink-surface px-4 py-3" data-testid={testid}>
    <div className="font-mono text-[9px] uppercase tracking-widest text-mute-dim">{label}</div>
    <div className={`mt-1 font-mono text-2xl font-bold ${cls}`}>{value}</div>
    {sub && <div className="mt-0.5 font-mono text-[9px] text-mute-dim">{sub}</div>}
  </div>
);

const MiniChart = ({ title, data, dataKey, color }) => (
  <div className="border border-white/10 bg-ink-surface p-4">
    <p className="mb-3 font-mono text-[9px] uppercase tracking-[0.25em] text-mute-dim">{title}</p>
    {data.length === 0 ? (
      <p className="py-10 text-center font-mono text-xs text-mute-dim">belum ada data</p>
    ) : (
      <div className="h-44">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={data} margin={{ top: 5, right: 5, left: -18, bottom: 0 }}>
            <defs>
              <linearGradient id={`g-${dataKey}`} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={color} stopOpacity={0.3} />
                <stop offset="100%" stopColor={color} stopOpacity={0} />
              </linearGradient>
            </defs>
            <XAxis dataKey="day" tick={{ fill: "#5C5C66", fontSize: 9, fontFamily: "JetBrains Mono" }} tickFormatter={(d) => d.slice(5)} />
            <YAxis tick={{ fill: "#5C5C66", fontSize: 9, fontFamily: "JetBrains Mono" }} allowDecimals={false} />
            <Tooltip contentStyle={{ background: "#0C0C10", border: "1px solid rgba(255,255,255,0.15)", fontFamily: "JetBrains Mono", fontSize: 11 }} />
            <Area type="monotone" dataKey={dataKey} stroke={color} fill={`url(#g-${dataKey})`} strokeWidth={1.5} />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    )}
  </div>
);

export const AdminMonitor = () => {
  const [data, setData] = useState(null);
  const [users, setUsers] = useState(null);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", tier: "FREE", verified: false, plan: "", price: "" });

  const load = useCallback(async () => {
    const [m, u] = await Promise.all([
      axios.get(`${API}/admin/monitor`, { headers: headers() }),
      axios.get(`${API}/admin/users`, { headers: headers() }),
    ]);
    setData(m.data);
    setUsers(u.data.items);
  }, []);

  useEffect(() => { load().catch(() => {}); }, [load]);

  const act = async (email, action) => {
    try {
      await axios.post(`${API}/admin/users/${encodeURIComponent(email)}/${action}`, {}, { headers: headers() });
      toast.success(action === "approve" ? "User disetujui — trial 3 hari aktif" : action === "disable" ? "User dinonaktifkan" : "User dipulihkan");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Aksi gagal");
    }
  };

  const toggleLock = async (u) => {
    try {
      await axios.patch(`${API}/admin/users/${encodeURIComponent(u.email)}`, { locked: !u.locked }, { headers: headers() });
      toast.success(u.locked ? "Kunci dibuka — sinyal user aktif kembali" : "User dikunci — sinyalnya terkunci");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Aksi gagal");
    }
  };

  const openEdit = (u) => {
    setEditing(u);
    setForm({ name: u.name || "", tier: u.tier || "FREE", verified: !!u.verified, plan: "", price: "" });
  };

  const saveEdit = async (e) => {
    e.preventDefault();
    const body = { name: form.name, tier: form.tier, verified: form.verified };
    if (form.plan) {
      body.plan = form.plan;
      if (form.price) body.price = parseInt(form.price, 10);
    }
    try {
      await axios.patch(`${API}/admin/users/${encodeURIComponent(editing.email)}`, body, { headers: headers() });
      toast.success(form.plan ? "User ditandai berbayar — masa aktif diperbarui" : "Data user diperbarui");
      setEditing(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Gagal menyimpan perubahan");
    }
  };

  const removeForever = async (email) => {
    if (!window.confirm(`Hapus PERMANEN ${email}? Akun dan pengaturannya dihapus selamanya dan tidak bisa dikembalikan.`)) return;
    try {
      await axios.delete(`${API}/admin/users/${encodeURIComponent(email)}`, { headers: headers() });
      toast.success("User dihapus permanen");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Gagal menghapus user");
    }
  };

  if (!data) return <p className="font-mono text-xs text-mute-dim">memuat monitoring...</p>;
  const s = data.summary;

  return (
    <div data-testid="admin-monitor">
      <SymbolsControl />
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-6">
        <Card label="Total User" value={s.total_users} testid="mon-total" />
        <Card label="User Aktif (Terverifikasi)" value={s.verified} cls="text-buy" sub={`${s.new_7d} baru 7 hari`} testid="mon-verified" />
        <Card label="Menunggu Verifikasi" value={s.unverified} cls="text-warn" testid="mon-pending" />
        <Card label="User Berbayar" value={s.paid} cls="text-[#8b97f5]" sub="paket 1m/3m/12m" testid="mon-paid" />
        <Card label="Dinonaktifkan" value={s.deleted} cls="text-sell" testid="mon-deleted" />
        <Card label="Kunjungan Hari Ini" value={data.visits.length ? data.visits[data.visits.length - 1].count : 0} testid="mon-visits" />
      </div>

      <div className="mt-3 grid gap-3 lg:grid-cols-3">
        <MiniChart title="Pendaftaran User / Hari" data={data.registrations} dataKey="count" color="#00E676" />
        <MiniChart title="User Aktif / Hari (pemakaian)" data={data.activity} dataKey="users" color="#5266eb" />
        <MiniChart title="Kunjungan Landing / Hari" data={data.visits} dataKey="count" color="#FFC107" />
      </div>

      {data.pending_verification.length > 0 && (
        <div className="mt-4 border border-warn/30 bg-warn/5 p-4" data-testid="pending-list">
          <p className="mb-2 font-mono text-[10px] font-bold uppercase tracking-widest text-warn">
            Menunggu verifikasi ({data.pending_verification.length})
          </p>
          {data.pending_verification.map((u) => (
            <div key={u.email} className="flex flex-wrap items-center justify-between gap-2 py-1">
              <p className="font-mono text-xs text-mute">· {u.name || "—"} — {u.email} <span className="text-mute-dim">({u.created_at?.slice(0, 10)})</span></p>
              <button onClick={() => act(u.email, "approve")} data-testid={`pending-approve-${u.email.split("@")[0]}`}
                className="flex items-center gap-1.5 border border-buy/40 px-2.5 py-1 font-mono text-[10px] font-bold text-buy transition-colors hover:bg-buy hover:text-ink">
                <BadgeCheck size={11} /> SETUJUI
              </button>
            </div>
          ))}
        </div>
      )}

      <div className="mt-4 overflow-x-auto border border-white/10" data-testid="admin-users-table">
        <table className="w-full min-w-[960px] text-left font-mono text-xs">
          <thead>
            <tr className="border-b border-white/10 bg-ink-surface text-[9px] uppercase tracking-widest text-mute-dim">
              <th className="px-4 py-3">User</th><th className="px-4 py-3">Telepon</th><th className="px-4 py-3">Status</th><th className="px-4 py-3">Tier</th>
              <th className="px-4 py-3">Masa Aktif</th><th className="px-4 py-3">Daftar</th><th className="px-4 py-3">Aktif Terakhir</th>
              <th className="px-4 py-3">Aksi</th>
            </tr>
          </thead>
          <tbody>
            {(users || []).map((u) => (
              <tr key={u.email} className={`border-b border-white/5 ${u.status === "deleted" ? "opacity-50" : ""}`} data-testid={`user-row-${u.email.split("@")[0]}`}>
                <td className="px-4 py-2.5">
                  <span className="font-bold text-white">{u.name || "—"}</span>
                  <span className="ml-2 text-mute-dim">{u.email}</span>
                </td>
                <td className="px-4 py-2.5 text-mute">{u.phone ? `+${u.phone}` : "—"}</td>
                <td className="px-4 py-2.5">
                  {u.status === "deleted" ? (
                    <span className="border border-sell/40 bg-sell/10 px-2 py-0.5 text-[9px] font-bold text-sell">DINONAKTIFKAN</span>
                  ) : u.locked ? (
                    <span className="border border-warn/40 bg-warn/10 px-2 py-0.5 text-[9px] font-bold text-warn" data-testid={`locked-badge-${u.email.split("@")[0]}`}>TERKUNCI</span>
                  ) : u.verified ? (
                    <span className="border border-buy/40 bg-buy/10 px-2 py-0.5 text-[9px] font-bold text-buy">TERVERIFIKASI</span>
                  ) : (
                    <span className="border border-warn/40 bg-warn/10 px-2 py-0.5 text-[9px] font-bold text-warn">PENDING</span>
                  )}
                </td>
                <td className="px-4 py-2.5 text-mute">
                  {u.tier || "FREE"}
                  {u.price_paid != null && (
                    <span className="block text-[9px] text-buy">Rp{Number(u.price_paid).toLocaleString("id-ID")}</span>
                  )}
                </td>
                <td className="px-4 py-2.5 text-mute-dim">{(u.access_expires || u.sub_expires)?.slice(0, 10) || "—"}</td>
                <td className="px-4 py-2.5 text-mute-dim">{u.created_at?.slice(0, 10)}</td>
                <td className="px-4 py-2.5 text-mute-dim">{u.last_active?.slice(5, 16).replace("T", " ") || "—"}</td>
                <td className="px-4 py-2.5">
                  <div className="flex flex-wrap items-center gap-1.5">
                    {u.status !== "deleted" && !u.verified && (
                      <button onClick={() => act(u.email, "approve")} data-testid={`approve-${u.email.split("@")[0]}`}
                        className="flex items-center gap-1 border border-buy/40 px-2 py-1 text-[10px] font-bold text-buy transition-colors hover:bg-buy hover:text-ink">
                        <BadgeCheck size={11} /> SETUJUI
                      </button>
                    )}
                    <button onClick={() => openEdit(u)} data-testid={`edit-${u.email.split("@")[0]}`}
                      className="flex items-center gap-1 border border-white/20 px-2 py-1 text-[10px] font-bold text-mute transition-colors hover:border-white/50 hover:text-white">
                      <Pencil size={11} /> EDIT
                    </button>
                    {u.status !== "deleted" && (
                      <button onClick={() => toggleLock(u)} data-testid={`lock-${u.email.split("@")[0]}`}
                        className={`flex items-center gap-1 border px-2 py-1 text-[10px] font-bold transition-colors ${
                          u.locked ? "border-buy/40 text-buy hover:bg-buy hover:text-ink" : "border-warn/40 text-warn hover:bg-warn hover:text-ink"
                        }`}>
                        {u.locked ? <><LockOpen size={11} /> BUKA</> : <><Lock size={11} /> KUNCI</>}
                      </button>
                    )}
                    {u.status === "deleted" ? (
                      <button onClick={() => act(u.email, "restore")} data-testid={`restore-${u.email.split("@")[0]}`}
                        className="flex items-center gap-1 border border-buy/40 px-2 py-1 text-[10px] font-bold text-buy transition-colors hover:bg-buy hover:text-ink">
                        <RotateCcw size={11} /> PULIHKAN
                      </button>
                    ) : (
                      <button onClick={() => act(u.email, "disable")} data-testid={`disable-${u.email.split("@")[0]}`}
                        className="flex items-center gap-1 border border-sell/40 px-2 py-1 text-[10px] font-bold text-sell transition-colors hover:bg-sell hover:text-white">
                        <Ban size={11} /> NONAKTIF
                      </button>
                    )}
                    <button onClick={() => removeForever(u.email)} data-testid={`delete-${u.email.split("@")[0]}`} title="Hapus permanen"
                      className="flex items-center gap-1 border border-sell/40 px-2 py-1 text-[10px] font-bold text-sell transition-colors hover:bg-sell hover:text-white">
                      <Trash2 size={11} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" data-testid="user-edit-modal">
          <form onSubmit={saveEdit} className="max-h-[90vh] w-full max-w-sm overflow-y-auto border border-white/15 bg-[#0d1219] p-5">
            <div className="flex items-center justify-between">
              <p className="font-mono text-xs font-bold uppercase tracking-widest text-white">Edit User</p>
              <button type="button" onClick={() => setEditing(null)} data-testid="user-edit-close"
                className="text-mute-dim transition-colors hover:text-white">
                <X size={15} />
              </button>
            </div>
            <p className="mt-1 truncate font-mono text-[10px] text-mute-dim">{editing.email}</p>
            <label className="mt-4 block font-mono text-[9px] uppercase tracking-widest text-mute-dim">Nama</label>
            <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="user-edit-name"
              className="mt-1 h-10 w-full border border-white/15 bg-ink px-3 font-mono text-xs text-white outline-none focus:border-buy/60" />
            <label className="mt-3 block font-mono text-[9px] uppercase tracking-widest text-mute-dim">Tier</label>
            <select value={form.tier} onChange={(e) => setForm({ ...form, tier: e.target.value })} data-testid="user-edit-tier"
              className="mt-1 h-10 w-full border border-white/15 bg-ink px-2 font-mono text-xs text-white">
              <option value="FREE">FREE</option>
              <option value="PREMIUM">PREMIUM</option>
            </select>
            <label className="mt-3 block font-mono text-[9px] uppercase tracking-widest text-mute-dim">Tandai Berbayar (paket)</label>
            <select value={form.plan}
              onChange={(e) => {
                const p = e.target.value;
                setForm({ ...form, plan: p, price: p ? String(PLAN_DEFAULT_PRICE[p]) : "" });
              }}
              data-testid="user-edit-plan"
              className="mt-1 h-10 w-full border border-white/15 bg-ink px-2 font-mono text-xs text-white">
              {PLANS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
            {form.plan && (
              <>
                <label className="mt-3 block font-mono text-[9px] uppercase tracking-widest text-mute-dim">
                  Harga dibayar (Rp) — bisa custom diskon
                </label>
                <input value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value.replace(/[^0-9]/g, "") })}
                  inputMode="numeric" data-testid="user-edit-price"
                  className="mt-1 h-10 w-full border border-white/15 bg-ink px-3 font-mono text-xs text-white outline-none focus:border-buy/60" />
              </>
            )}
            <label className="mt-3 flex cursor-pointer items-center gap-2 font-mono text-xs text-mute">
              <input type="checkbox" checked={form.verified} onChange={(e) => setForm({ ...form, verified: e.target.checked })} data-testid="user-edit-verified" />
              Email terverifikasi
            </label>
            <button type="submit" data-testid="user-edit-save"
              className="mt-5 flex h-10 w-full items-center justify-center border border-buy bg-buy/10 font-mono text-xs font-bold uppercase tracking-wider text-buy transition-colors hover:bg-buy hover:text-ink">
              Simpan
            </button>
          </form>
        </div>
      )}
    </div>
  );
};
